import React, { useCallback, useMemo, useState } from 'react';
import RxntService from '../../services/RxntService';
import { OK } from '../../utils/constant';
import {
  clearRxntBrowserSessionActive,
  isRxntBrowserSessionActive,
  markRxntBrowserSessionActive,
  openRxntDeepLink,
  resolveRxntDeepLinkUrl,
} from '../../utils/rxntDeepLinkUtils';
import { mapGatewayPatientToSdk, rxntGatewayPatientId } from '../../utils/rxntLaunchUtils';
import { defaultRxntLaunchReturnUrl } from '../../utils/rxntLaunchSdk';
import { performRxntSdkLaunch } from '../../utils/rxntSdkLaunch';

/** Host for POST /ehr-gateway/launch (not the API path prefix). */
const RXNT_LAUNCH_BASE_URL =
  process.env.REACT_APP_RXNT_LAUNCH_BASE_URL || 'https://app2.rxnt.com';

const DEFAULT_BUTTON_CLASS = 'btn btn-primary sh_btnprimary btn-sm';

const RxntBlockedIcon = () => (
  <i className="fas fa-ban me-1" aria-hidden="true" />
);

/**
 * RXNT EHR launch: custom form POST into named popup (rxnt_ehr).
 * RXNT's sdk.min.js uses target="_blank" (new tab); we mirror its fields with target=rxnt_ehr.
 *
 * After first launch, legacy deep links open in the same popup when session TTL is active.
 */
const RxntLaunchButton = ({
  text = 'Open RXNT',
  providerId = '',
  rxntDoctorId = null,
  employeeId = null,
  rxntPatientId = null,
  gatewayPatient = null,
  returnUrl,
  disabled = false,
  disabledTitle = 'Select a mapped provider first',
  className = '',
  buttonClassName = DEFAULT_BUTTON_CLASS,
  linkTarget = 'auto',
  forceSdkLaunch = false,
  showBlockedIconWhenDisabled = false,
  onError,
  onLaunchSuccess,
}) => {
  const [launching, setLaunching] = useState(false);
  const [sessionTick, setSessionTick] = useState(0);

  const resolvedRxntPatientId = useMemo(() => {
    if (rxntPatientId != null && rxntPatientId !== '') {
      const n = Number(rxntPatientId);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    if (gatewayPatient) {
      return rxntGatewayPatientId({ patient: gatewayPatient, patientId: gatewayPatient.patientId });
    }
    return null;
  }, [rxntPatientId, gatewayPatient]);

  const deepLinkUrl = useMemo(
    () => resolveRxntDeepLinkUrl(linkTarget, resolvedRxntPatientId),
    [linkTarget, resolvedRxntPatientId]
  );

  const browserSessionActive = useMemo(
    () => isRxntBrowserSessionActive(),
    [sessionTick]
  );

  const useDeepLink =
    !forceSdkLaunch &&
    linkTarget !== 'sdk' &&
    browserSessionActive &&
    Boolean(deepLinkUrl) &&
    !disabled;

  const canLaunch =
    !disabled &&
    !launching &&
    providerId &&
    rxntDoctorId != null &&
    rxntDoctorId !== '';

  const buildLaunchData = useCallback(async () => {
    const launchReturnUrl = returnUrl || defaultRxntLaunchReturnUrl();
    const contextParams = { return_url: launchReturnUrl };
    if (providerId) contextParams.provider_id = providerId;
    if (rxntDoctorId != null && rxntDoctorId !== '') {
      contextParams.rxnt_doctor_id = rxntDoctorId;
    }
    if (employeeId) contextParams.employee_id = employeeId;
    if (rxntPatientId != null && rxntPatientId !== '') {
      contextParams.rxnt_patient_id = rxntPatientId;
    }

    const res = await RxntService.getLaunchContext(contextParams);
    if (res?.code !== OK || !res?.data?.launchData) {
      throw new Error(res?.message || 'Failed to build RXNT launch context');
    }
    const launchData = { ...res.data.launchData };
    if (!launchData.patientData && gatewayPatient) {
      const chart =
        employeeId || gatewayPatient.chartNumber || gatewayPatient.chartNo || '';
      const mapped = mapGatewayPatientToSdk(gatewayPatient, chart);
      if (mapped) launchData.patientData = mapped;
    }
    return launchData;
  }, [
    returnUrl,
    providerId,
    rxntDoctorId,
    employeeId,
    rxntPatientId,
    gatewayPatient,
  ]);

  const runPopupLaunch = useCallback(async () => {
    if (!canLaunch) return;
    setLaunching(true);
    try {
      await performRxntSdkLaunch({
        baseUrl: RXNT_LAUNCH_BASE_URL.replace(/\/$/, ''),
        getAccessToken: async () => {
          const res = await RxntService.getLaunchToken();
          if (res?.code !== OK || !res?.data?.accessToken) {
            throw new Error(res?.message || 'Failed to obtain RXNT access token');
          }
          return res.data.accessToken;
        },
        getLaunchData: buildLaunchData,
        onLaunchSuccess: () => {
          setSessionTick((t) => t + 1);
          if (onLaunchSuccess) onLaunchSuccess();
        },
        onError,
      });
    } finally {
      setLaunching(false);
    }
  }, [canLaunch, buildLaunchData, onLaunchSuccess, onError]);

  const forceSdkReconnect = useCallback(() => {
    clearRxntBrowserSessionActive();
    setSessionTick((t) => t + 1);
    runPopupLaunch();
  }, [runPopupLaunch]);

  const handleDeepLinkClick = useCallback(
    (e) => {
      if (!deepLinkUrl || disabled || launching) return;
      if (e?.shiftKey || e?.altKey || !isRxntBrowserSessionActive()) {
        forceSdkReconnect();
        return;
      }
      const opened = openRxntDeepLink(deepLinkUrl);
      if (!opened && onError) {
        onError('Allow pop-ups for Snap to open the RXNT window.');
      }
    },
    [deepLinkUrl, disabled, launching, forceSdkReconnect, onError]
  );

  const handleSdkClick = useCallback(
    (e) => {
      if (e?.shiftKey || e?.altKey) {
        clearRxntBrowserSessionActive();
        setSessionTick((t) => t + 1);
      }
      runPopupLaunch();
    },
    [runPopupLaunch]
  );

  const wrapClass = ['rxnt-launch-wrap', 'position-relative', 'd-inline-flex', className]
    .filter(Boolean)
    .join(' ');

  const reconnectHint = 'Shift+click to reconnect';

  const deepLinkTitle = resolvedRxntPatientId
    ? `Open patient in RXNT. ${reconnectHint}`
    : `Open pending queue. ${reconnectHint}`;

  const sdkTitle = `Open RXNT. ${reconnectHint}`;

  const resolveTitle = (activeTitle, isControlDisabled) => {
    if (launching) return 'Opening…';
    if (isControlDisabled) return disabledTitle;
    return activeTitle;
  };

  const deepLinkDisabled = disabled || launching;
  const sdkDisabled = !canLaunch;
  const showBlockedIcon =
    showBlockedIconWhenDisabled &&
    (useDeepLink ? deepLinkDisabled && !launching : sdkDisabled && !launching);

  const renderButtonLabel = (label) => (
    <>
      {showBlockedIcon ? <RxntBlockedIcon /> : null}
      {label}
    </>
  );

  if (useDeepLink) {
    return (
      <span className={wrapClass}>
        <button
          type="button"
          className={buttonClassName}
          title={resolveTitle(deepLinkTitle, deepLinkDisabled)}
          disabled={deepLinkDisabled}
          onClick={handleDeepLinkClick}
        >
          {renderButtonLabel(text)}
        </button>
      </span>
    );
  }

  return (
    <span className={wrapClass}>
      <button
        type="button"
        className={buttonClassName}
        disabled={sdkDisabled}
        title={resolveTitle(sdkTitle, sdkDisabled)}
        onClick={handleSdkClick}
      >
        {renderButtonLabel(launching ? 'Opening…' : text)}
      </button>
    </span>
  );
};

export default RxntLaunchButton;
